'use client'

import Text from '../../text'
import { Button } from '@/components/ui/button'
import { Dictionary } from '@/localization/dictionary'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import DefaultLoadingSpinner from '../../loading-spinners/default-loading-spinner'
import { resetPasswordAction } from './reset-password-dialog.action'
import { showToast } from '@/lib/utils/toast'
import { useAuthDialogStore } from '../auth-dialog.store'
import useSafeAction from '@/lib/safe-action/use-safe-action'
import {
  getResetPasswordFormSchema,
  resetPasswordFormSchema,
} from '@/lib/api/shopware/account/account.schemas'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Dialog, DialogContent, DialogClose, DialogTitle } from '@/components/ui/dialog'
import { X } from 'lucide-react'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect } from 'react'
import PasswordInput from '../../password-input'

interface ResetPasswordDialogProps {
  dict: Dictionary['passwordResetForm']
}

const ResetPasswordDialog = ({ dict }: ResetPasswordDialogProps) => {
  const showResetPassword = useAuthDialogStore()((s) => s.showResetPassword)
  const setShowPasswordReset = useAuthDialogStore()(
    (s) => s.setShowPasswordReset,
  )

  const searchParams = useSearchParams()
  const path = usePathname()
  const hash = searchParams.get('hash')
  const shouldShowDialog = hash && path.includes('/account/recover/password')

  useEffect(() => {
    if (shouldShowDialog) {
      setShowPasswordReset(true)
    }
  }, [shouldShowDialog, setShowPasswordReset])

  const form = useForm<z.infer<typeof resetPasswordFormSchema>>({
    resolver: zodResolver(getResetPasswordFormSchema(dict)),
    defaultValues: {
      newPassword: '',
    },
  })

  const { execute, isExecuting } = useSafeAction(resetPasswordAction, {
    onSuccess: () => {
      showToast(dict.alert.success)
      setShowPasswordReset(false)
    },
  })

  const onSubmit = async (values: z.infer<typeof resetPasswordFormSchema>) => {
    if (hash) {
      execute({
        hash,
        newPassword: values.newPassword,
        newPasswordConfirm: values.newPassword,
      })
    }
  }

  return (
    <Dialog
      open={showResetPassword}
      onOpenChange={() => setShowPasswordReset(false)}
    >
      <DialogContent aria-describedby={undefined} className="p-0 max-sm:h-screen max-sm:w-screen max-sm:max-w-none sm:max-w-[451px]">
        <DialogTitle className="sr-only">{dict.title}</DialogTitle>
        <div className="flex flex-col items-center px-5 pb-12.5 pt-6 sm:px-13.5 sm:py-6">
          <div className="mb-7 flex w-full justify-end">
            <DialogClose>
              <X className="size-6 text-black" strokeWidth={1} />
            </DialogClose>
          </div>
          <Text
            as="h2"
            size={31}
            lineHeight={34.27}
            uppercase
            markdown
            className="mb-7.5 text-center font-normal text-secondary-900"
          >
            {dict.title}
          </Text>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex w-full flex-col gap-5"
            >
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem className="grid w-full items-center">
                    <FormLabel className="text-xs font-normal">
                      {dict.newPassword}
                    </FormLabel>

                    <FormControl>
                      <PasswordInput autoComplete="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full">
                {isExecuting ? (
                  <DefaultLoadingSpinner />
                ) : (
                  <Text as="p" uppercase>
                    {dict.resetPassword}
                  </Text>
                )}
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ResetPasswordDialog
